import { cn, Icon } from 'corso-admin-ui';
import {
  ComponentPropsWithoutRef,
  JSX,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { Link, LinkProps } from 'react-router-dom';

/** Minimalist wrapper that can be used alike a card. */
function GridListWrapper({ children }: PropsWithChildren) {
  return (
    <div className="overflow-hidden shadow-md sm:rounded-lg">{children}</div>
  );
}

const variantStyles = {
  DEFAULT:
    'bg-neutral-50 p-3 text-neutral-700 group-hover/grid-list-item-link:bg-neutral-700 group-hover/grid-list-item-link:text-neutral-50',
  primary:
    'bg-corso-blue-50 p-3 text-corso-blue-700 group-hover/grid-list-item-link:bg-corso-blue-700 group-hover/grid-list-item-link:text-corso-blue-50',
} as const;

type GridListItemVisualProps = {
  label: string;
  description?: ReactNode;
  icon?: ReactNode;
  secondaryIcon?: JSX.Element;
  variant?: keyof typeof variantStyles;
};

/** Shared visual slot for `GridList.ItemLink` and `GridList.ItemButton`. */
function GridListItemVisual({
  label,
  description,
  icon,
  secondaryIcon,
  variant = 'DEFAULT',
}: GridListItemVisualProps) {
  return (
    <>
      <div className="flex items-center gap-4">
        {icon && (
          <span
            className={cn(
              'inline-flex rounded-lg transition-all duration-300 *:size-5',
              variantStyles[variant],
            )}
          >
            {icon}
          </span>
        )}
        <div className="space-y-2">
          <p className="text-sm leading-6 font-semibold text-pretty text-neutral-600">
            {label}
          </p>
          {description && (
            <p className="text-sm text-balance text-neutral-500">
              {description}
            </p>
          )}
        </div>
      </div>
      <span
        className={cn(
          'pointer-events-none text-neutral-300 *:h-6 *:w-6 group-hover/grid-list-item-link:text-neutral-400',
          !icon && 'self-center',
        )}
      >
        {secondaryIcon ?? <Icon icon="arrowRight" aria-hidden="true" />}
      </span>
    </>
  );
}

function GridListItemLink({
  to,
  label,
  description,
  icon,
  secondaryIcon,
  variant,
  children,
}: PropsWithChildren<
  GridListItemVisualProps & {
    to: LinkProps['to'];
  }
>) {
  return (
    <div className="flex flex-col bg-white has-[.link:focus]:ring-2 has-[.link:focus]:ring-corso-blue-700 has-[.link:focus]:ring-inset">
      <Link
        to={to}
        className="link group/grid-list-item-link grid h-full grid-cols-[1fr_auto] justify-between gap-2 p-4 focus:outline-hidden"
      >
        <GridListItemVisual
          label={label}
          description={description}
          icon={icon}
          secondaryIcon={secondaryIcon}
          variant={variant}
        />
      </Link>
      {children && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
}

function GridListItemButton({
  onClick,
  type = 'button',
  label,
  description,
  icon,
  secondaryIcon,
  variant,
  children,
}: PropsWithChildren<
  GridListItemVisualProps &
    Pick<ComponentPropsWithoutRef<'button'>, 'onClick' | 'type'>
>) {
  return (
    <div className="flex flex-col bg-white has-[.link:focus]:ring-2 has-[.link:focus]:ring-corso-blue-700 has-[.link:focus]:ring-inset">
      <button
        // oxlint-disable-next-line react/button-has-type -- `type` has a default of 'button'
        type={type}
        onClick={onClick}
        className="link group/grid-list-item-link grid h-full grid-cols-[1fr_auto] justify-between gap-2 p-4 text-left focus:outline-hidden"
      >
        <GridListItemVisual
          label={label}
          description={description}
          icon={icon}
          secondaryIcon={secondaryIcon}
          variant={variant}
        />
      </button>
      {children && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
}

export default function GridList<T>({
  columns = 2,
  data,
  children: renderChild,
}: {
  columns?: number;
  data: T[];
  children: (value: T) => ReactNode;
}) {
  const columnSpan = columns - (data.length % columns);

  return (
    <div
      style={{
        '--column-count': columns,
      }}
      // uses grid gap, and background color to emulate border between cards with border radius
      className="sm:grid-cols divide-y divide-neutral-200 bg-neutral-200 sm:grid sm:grid-cols-[repeat(var(--column-count),minmax(0,1fr))] sm:gap-px sm:divide-y-0 sm:rounded-lg"
    >
      {data.map(renderChild)}
      {!!columnSpan && columnSpan !== columns && (
        <div
          // automatically pad out the last row to fill the grid after the `sm` breakpoint
          style={{ '--column-span': columnSpan }}
          className="col-[span_var(--column-span)/span_var(--column-span)] m-0 hidden bg-white md:block"
        />
      )}
    </div>
  );
}

GridList.ItemLink = GridListItemLink;
GridList.ItemButton = GridListItemButton;
GridList.Wrapper = GridListWrapper;
